/**
 * Every request the user screens make, and nothing else makes any.
 *
 * Two audiences, two permissions, one module because they are two halves of one
 * subject. The `/api/users` calls are Administrator-only on the server and the
 * navigation hides the screen; the `/api/account` calls are every signed-in
 * person, Viewers included, because a person's own password is not business
 * data.
 *
 * **There is no delete.** Not omitted here: there is no endpoint. An account
 * that signed off a training record is part of that record's defensibility, so
 * access is withdrawn and the row stays.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api, expectBody } from "@/api/client"
import { PAGE_SIZE } from "@/lib/constants"
import { sessionKey, type Me } from "@/lib/session"

import type { Role } from "./model"

export const userKeys = {
  all: ["users"] as const,
  list: (page: number, ordering: string) => ["users", "list", page, ordering] as const,
}

export function useUsers(page: number, ordering: string) {
  return useQuery({
    queryKey: userKeys.list(page, ordering),
    queryFn: async () => {
      const { data, response } = await api.GET("/api/users", {
        params: {
          query: { page, page_size: PAGE_SIZE, ordering: ordering as "username" },
        },
      })
      return expectBody(data, response, "the people who can sign in")
    },
    placeholderData: (previous) => previous,
  })
}

/**
 * What a new account is made of.
 *
 * No `system_id`: the server assigns it and this screen could not predict one
 * that would hold. `employee_id` is nullable rather than optional because not
 * every account is one of the workforce, and a vendor's unlinked account is an
 * answer rather than an omission.
 */
export type NewUser = {
  username: string
  first_name: string
  last_name: string
  role: Role
  password: string
  employee_id: number | null
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: NewUser) => {
      const { data, response } = await api.POST("/api/users", { body })
      return expectBody(data, response, "adding this person")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

export function useChangeRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, role }: { id: number; role: Role }) => {
      const { data, response } = await api.PUT("/api/users/{user_id}/role", {
        params: { path: { user_id: id } },
        body: { role },
      })
      return expectBody(data, response, "changing this role")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

/**
 * Withdraw or restore access.
 *
 * One hook for both directions because they are one act with a sign, and the
 * screen's confirm dialog is the only place they differ.
 */
export function useSetAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      // Two calls rather than one with the path in a variable: each generated
      // path is its own type, and a union of them loses the body's shape.
      const { data, response } = active
        ? await api.POST("/api/users/{user_id}/reactivate", {
            params: { path: { user_id: id } },
          })
        : await api.POST("/api/users/{user_id}/deactivate", {
            params: { path: { user_id: id } },
          })
      return expectBody(data, response, "changing this person's access")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

/**
 * Change your own password.
 *
 * Nothing is invalidated on success and nothing needs to be: the server rotates
 * the session's auth hash so this browser stays signed in, and no cached query
 * holds a password.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { current_password: string; new_password: string }) => {
      await api.POST("/api/account/password", { body })
    },
  })
}

/**
 * Rename your own account.
 *
 * The answer is the session payload, so it is written straight into the session
 * query: the top bar relabels itself as the dialog closes, rather than showing
 * the old name until something else happens to refetch.
 */
export function useChangeUsername() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (username: string) => {
      const { data, response } = await api.POST("/api/account/username", {
        body: { username },
      })
      return expectBody(data, response, "changing your username")
    },
    onSuccess: (me: Me) => {
      queryClient.setQueryData(sessionKey, me)
    },
  })
}
